import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { Info, Loader2 } from "lucide-react";

import { auth } from "../../firebaseConfig";
import Button from "../../lib/ui/components/button/Button";
import "./LoginHero.css";
import { ReactComponent as JuneLogo } from "../../assests/logo.svg";

import { useStore } from "../../store";
import { ME } from "../../lib/urls";


const Login = () => {
  const setUid = useStore((state) => state.setUid);
  const setCompany = useStore((state) => state.setCompany);
  const logoutReason = useStore((state) => state.logoutReason);
  const setLogoutReason = useStore((state) => state.setLogoutReason);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(""); // Clear previous errors
    
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      setUid(user.uid);
      
      await fetchMe(await user.getIdToken());

      // Clear logout reason after successful login
      setLogoutReason(null);

      // Get redirect URL from query params
      const searchParams = new URLSearchParams(location.search);
      const redirectUrl = searchParams.get('redirect');
      
      if (redirectUrl) {
        // Redirect to the original page (already decoded by URLSearchParams)
        navigate(redirectUrl);
      } else {
        // Default redirect to jobs page
        navigate('/jobs');
      }
    } catch (error) {
      console.log("Error during login:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMe = async (token) => {
    const maxRetries = 4;
    const timeoutMs = 8000;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        
        try {
          const response = await fetch(ME, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal
          });
          
          if (response.ok) {
            const data = await response.json();
            setCompany(data?.company);
            return data;
          } else {
            throw new Error(`Server error`);
          }
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        if (attempt === maxRetries) {
          // Final attempt failed
          throw new Error("Unable to connect. Please try again.");
        }
        
        // Wait before retry: 1s, 2s, 4s
        const delay = 1000 * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  return (
    <div className="login-hero min-h-screen flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md space-y-6">
        <div className="space-y-1 text-center flex flex-col items-center">
          <JuneLogo className="h-12 w-auto" />
          <p className="text-small text-purple-950/75">
            Drastically Improved Time-to-fill for Recruiting Teams.
          </p>
        </div>

          {/* Timeout Alert Message */}
          {logoutReason === "timeout" && (
            <div className="max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-blue-400" aria-hidden />
                </div>
                <div className="ml-3">
                  <p className="text-small text-blue-700">
                    You've been logged out after being inactive for a bit. Just
                    sign in again to keep using June.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-md mx-auto bg-white shadow-md rounded-lg">
            <div className="pt-6 px-6 pb-6">
              <form className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-small font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    data-cy="email"
                    type="email"
                    required
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-small font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    data-cy="password"
                    type="password"
                    required
                    placeholder="Enter your password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <div className="text-right">
                    <a
                      href="/reset-password"
                      className="text-small text-blue-600 hover:text-blue-800"
                    >
                      Forgot Password?
                    </a>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full flex justify-center items-center"
                  disabled={isLoading}
                  onClick={handleLogin}
                  variant="primary"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Logging in...
                    </>
                  ) : (
                    "Log in"
                  )}
                </Button>
                {error && <div className="text-red-500 text-small">{error}</div>}
              </form>
            </div>
          </div>
      </div>
    </div>
  );
};

export default Login;
